// @flow

import React from 'react';
import { I18n } from 'react-redux-i18n';
import type { ReconciliationStore } from '../../../../types/shoppingCenterType';
import { reconciliationAction, reconciliationFlag, reconciliationStates } from '../../../../const';
import RippleButton from '../../../../components/RippleButton';
import { timestampToISO } from '../../../../utils/dateUtils';
import EllipsisLoader from '../../../../components/EllipsisLoader';
import { isMappable } from '../gemini/geminiFunctions';
import MappableStoreButton from './MappableStoreButton';

type Props = {
  selectedStore: ReconciliationStore,
  saveStoreMapping: Function,
  possibleStoresList: ReconciliationStore[],
  oldTenant: ReconciliationStore,
  unMap: Function,
  isLoading: boolean,
  unselectStore: () => void,
}

function RightActionMappingMode({
                                  selectedStore,
                                  saveStoreMapping,
                                  possibleStoresList,
                                  oldTenant,
                                  unMap,
                                  isLoading,
                                  unselectStore,
                                }: Props) {
  const saveFlag = (flag: string) => {
    if (selectedStore.flagState === flag) {
      unMap(selectedStore);
    } else {
      saveStoreMapping(
        selectedStore,
        null,
        null,
        flag,
      );
    }
  };

  const mappingTitle = (store) => {
    if (isMappable(store.state)) {
      return (<span>{I18n.t(`MAPPING_MODE.${store.state}`)}</span>);
    }
    if (store.state === reconciliationStates.FLAGGED) {
      return (<span>{I18n.t(`MAPPING_MODE.${store.flagState}`)}</span>);
    }
    if (store.state === reconciliationStates.MAPPED) {
      return (<span>{store.mercuryName} [{store.idMercury}]</span>);
    }
    return undefined;
  };

  const storeInformations = () => {
    if (
      selectedStore.state === reconciliationStates.LUX_ONLY
      || selectedStore.state === reconciliationStates.FLAGGED
    ) {
      return (
        <>
          <span className="mapping-section-header">{I18n.t('MAPPING_MODE.LUX_ONLY_WARNING_TEXT')}</span>
          <div className="action-flagging-mode">
            <span className="warning">{I18n.t('MAPPING_MODE.LUX_ONLY_STORE_CMS', {
              idStoreCms: selectedStore.idStoreCms,
            })}
            </span>
            <span className="warning">{I18n.t('MAPPING_MODE.LUX_ONLY_VISIOGLOBE_ID', {
              idVisioglobe: selectedStore.idVisioglobes[0],
            })}
            </span>
            <span className="warning">{I18n.t('MAPPING_MODE.LUX_ONLY_CURRENT_ID', {
              retailerId: selectedStore.idRetailer,
            })}
            </span>
            <span className="warning">{I18n.t('MAPPING_MODE.LUX_ONLY_CURRENT_NAME', { luxName: selectedStore.luxName })}
            </span>
            <span className="information-text">{I18n.t('MAPPING_MODE.flagStore')}</span>
            <div className="flags">
              <button
                className={`flag ${selectedStore.flagState === reconciliationFlag.VACANT ? 'selected' : ''}`}
                onClick={() => saveFlag(reconciliationFlag.VACANT.toString())}
              >
                {I18n.t(`MAPPING_MODE.${reconciliationFlag.VACANT}`)}
              </button>
              <button
                className={`flag ${selectedStore.flagState === reconciliationFlag.CLOSING ? 'selected' : ''}`}
                onClick={() => saveFlag(reconciliationFlag.CLOSING.toString())}
              >
                {I18n.t(`MAPPING_MODE.${reconciliationFlag.CLOSING}`)}
              </button>
              <button
                className={`flag ${selectedStore.flagState === reconciliationFlag.OPENING ? 'selected' : ''}`}
                onClick={() => saveFlag(reconciliationFlag.OPENING.toString())}
              >
                {I18n.t(`MAPPING_MODE.${reconciliationFlag.OPENING}`)}
              </button>
              <button
                className={`flag ${selectedStore.flagState === reconciliationFlag.OTHER ? 'selected' : ''}`}
                onClick={() => saveFlag(reconciliationFlag.OTHER.toString())}
              >
                {I18n.t(`MAPPING_MODE.${reconciliationFlag.OTHER}`)}
              </button>
            </div>
          </div>
        </>
      );
    }
    if (isMappable(selectedStore.state)) {
      return (
        <>
          <span className="mapping-section-header">
            {I18n.t('MAPPING_MODE.storeInformation')}
          </span>
          <div className="store-informations">
            {
              selectedStore.state !== reconciliationStates.EMPTY_UNIT
              && (
                <span className="warning">
                    {I18n.t(
                      'MAPPING_MODE.LUX_ONLY_STORE_CMS',
                      { idStoreCms: selectedStore.idStoreCms },
                    )}
                </span>
              )
            }
            <span className="warning">
              {
                I18n.t(
                  'MAPPING_MODE.LUX_ONLY_VISIOGLOBE_ID',
                  { idVisioglobe: selectedStore.idVisioglobes.join(', ') },
                )
              }
            </span>
            {
              oldTenant
              && (<span className="warning">{I18n.t('MAPPING_MODE.OLD_TENANT', { oldTenant })}</span>)
            }
          </div>
          {
            selectedStore.state === reconciliationStates.MULTIPLE_LUX && (
              <>
                <span className="mapping-section-header">{I18n.t('MAPPING_MODE.otherStoreInformation')}</span>
                <div className="store-informations">
                  {
                    possibleStoresList
                    && possibleStoresList.length > 0
                    && possibleStoresList[0].possibleLuxData
                      .filter(store => store.idStoreCms !== selectedStore.idStoreCms)
                      .map(store => (
                        <div key={store.idStoreCms}>
                          <span className="warning">{I18n.t('MAPPING_MODE.LUX_ONLY_STORE_CMS', {
                            idStoreCms: store.idStoreCms,
                          })}
                          </span>
                          <br />
                          <span className="warning">{I18n.t('MAPPING_MODE.LUX_ONLY_VISIOGLOBE_ID', {
                            idVisioglobe: store.idVisioglobes.join(', '),
                          })}
                          </span>
                          <br />
                          <br />
                        </div>
                      ))
                  }
                </div>
              </>
            )}
        </>
      );
    }
  };

  const hasMatchingMercuryStores = isMappable(selectedStore.state)
    && possibleStoresList.flat(str => str.idRetailer)
      .filter((value, index, self) => self.indexOf(value) === index).length > 1;

  const titleTab = (store) => {
    if (store.mercuryName) {
      return `${store.mercuryName} [${store.idMercury}] (${store.idVisioglobes.join(', ')})`;
    }
    if (store.luxName) {
      return `${store.luxName} (${store.idVisioglobes.join(', ')})`;
    }
    return `(${store.idVisioglobes.join(', ')})`;
  };

  return (
    <div className="right data-mapping-mode">
      {
        selectedStore && (
          <div className="content-data-mapping-mode">
            <div className="mapping-mode-top-part">
              <div className="mapping-left-tab-header">
                {titleTab && <span className="title-tab">{titleTab(selectedStore)}</span>}
                <button
                  onClick={unselectStore}
                  className="fa fa-times mapping-left-close"
                />
              </div>
              <div className="mapping-section-header">
                {selectedStore && mappingTitle(selectedStore)}
              </div>
              <span className="store-informations">
                {I18n.t(`MAPPING_MODE.informationText.${selectedStore.state}`)}
              </span>
              {
                isLoading
                  ? (<EllipsisLoader />)
                  : (
                    <>
                      {storeInformations()}
                      {
                        selectedStore.state === reconciliationStates.MAPPED
                        && (
                          <div className="action-mapping-mode">
                            <RippleButton
                              className="unmap-btn"
                              onClick={() => unMap(selectedStore)}
                            >
                              {I18n.t('MAPPING_MODE.unMap')}
                            </RippleButton>
                          </div>
                        )
                      }
                      {
                        isMappable(selectedStore.state) && !hasMatchingMercuryStores && (
                          <span className="store-informations">
                            {I18n.t('MAPPING_MODE.sameRetailer', { count: possibleStoresList.length })}
                          </span>
                        )
                      }
                    </>
                  )
              }
            </div>
            {
              !isLoading && (
                <div className="action-mapping-mode possible-mercury">
                  <div className="mapping-section-header">{I18n.t('MAPPING_MODE.possibleMercury')}</div>
                  <div className="mercury-only">
                    {
                      possibleStoresList
                      && possibleStoresList.length > 0
                      && possibleStoresList
                        .filter(store => !!store.mercuryName)
                        .sort((a, b) => a.mercuryName.localeCompare(b.mercuryName))
                        .map(store => (
                          <MappableStoreButton
                            key={store.id}
                            store={store}
                            mapOnClick={() => saveStoreMapping(
                              store,
                              selectedStore.idStoreCms,
                              selectedStore.idVisioglobes,
                              reconciliationAction.MAPPED,
                            )}
                          />
                        ))}
                  </div>
                </div>
              )
            }
            {
              selectedStore.mappingHistory
              && selectedStore.mappingHistory.length > 0
              && (
                <div className="history-container">
                  <div className="mapping-section-header">{I18n.t('MAPPING_MODE.historyTitle')}</div>
                  {
                    selectedStore.mappingHistory.map(history => (
                      <div
                        className="history"
                        key={history.id}
                      >
                        <span>
                          {
                            I18n.t(
                              'MAPPING_MODE.history',
                              {
                                state: history.state,
                                userName: history.userName,
                              },
                            )}
                        </span>
                        <span>{timestampToISO(history.date)}</span>
                      </div>
                    ))
                  }
                </div>
              )}
          </div>
        )
      }
    </div>
  );
}

export default RightActionMappingMode;

